import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";

const roles = [
  { value: "vc", label: "Venture capital" },
  { value: "pe", label: "Private equity" },
  { value: "family_office", label: "Family office" },
  { value: "aif", label: "Cat II AIF" },
  { value: "credit", label: "Bank / NBFI credit" },
  { value: "equity_research", label: "Equity research" },
  { value: "other", label: "Other" },
];

export default function Signup() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    role: "vc",
    notes: "",
  });

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/demo-requests", form);
      toast.success("Early access request received. We will email you.");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit early access request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 bg-[var(--cream)] text-[var(--ink)] lg:grid-cols-2" data-testid="signup-page">
      <div className="flex flex-col justify-center px-6 py-6 order-2 lg:order-1 lg:px-14 xl:px-20">
        <Link to="/" className="mb-7 flex w-fit items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[var(--warm-grey)] transition-colors hover:text-[var(--ink)]" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="smallcaps text-[var(--saffron)]">Early access</div>
        <h1 className="mt-3 font-serif text-[36px] font-medium leading-[1.05] tracking-[-0.018em]" data-testid="signup-title">Request early access.</h1>
        <p className="mt-2 max-w-2xl text-[13px] leading-[1.6] text-[var(--warm-grey)]">
          Tell us where financial data context and documentation review slow down your team.
        </p>

        <form onSubmit={onSubmit} className="mt-5 w-full max-w-2xl space-y-4 border border-[var(--border)] bg-white p-6 shadow-[0_18px_50px_rgba(17,24,39,.06)]">
          <div>
            <label className="smallcaps text-[var(--warm-grey)]">Full name</label>
            <input
              required
              value={form.full_name}
              onChange={update("full_name")}
              className="mt-1 w-full border-b border-[var(--border)] bg-white px-1 py-2.5 font-mono text-[15px] transition-colors focus:border-[var(--saffron)] focus:outline-none"
              data-testid="signup-name-input"
            />
          </div>
          <div>
            <label className="smallcaps text-[var(--warm-grey)]">Work email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              className="mt-1 w-full border-b border-[var(--border)] bg-white px-1 py-2.5 font-mono text-[15px] transition-colors focus:border-[var(--saffron)] focus:outline-none"
              data-testid="signup-email-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="smallcaps text-[var(--warm-grey)]">Firm</label>
              <input
                value={form.company}
                onChange={update("company")}
                className="mt-1 w-full border-b border-[var(--border)] bg-white px-1 py-2.5 font-mono text-[15px] transition-colors focus:border-[var(--saffron)] focus:outline-none"
                data-testid="signup-company-input"
              />
            </div>
            <div>
              <label className="smallcaps text-[var(--warm-grey)]">Desk</label>
              <select
                value={form.role}
                onChange={update("role")}
                className="mt-1 w-full border-b border-[var(--border)] bg-white px-1 py-2.5 text-[14px] transition-colors focus:border-[var(--saffron)] focus:outline-none"
                data-testid="signup-role-select"
              >
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="smallcaps text-[var(--warm-grey)]">Note for the team</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={update("notes")}
              className="mt-1 w-full resize-none border-b border-[var(--border)] bg-white px-1 py-2.5 text-[14px] leading-[1.5] transition-colors focus:border-[var(--saffron)] focus:outline-none"
              placeholder="Tell us what you want to discuss: documents, data sources, deployment needs, timelines, or a specific finance workflow."
              data-testid="signup-notes-input"
            />
          </div>
          <p className="border border-[var(--border)] bg-[var(--cream)] p-3 text-[12px] leading-[1.55] text-[var(--warm-grey)]">
            Early access details and next steps will be emailed to your work address.
          </p>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--night)] py-3.5 text-[14px] font-semibold tracking-wide text-white transition-colors duration-200 hover:bg-[var(--ink)] disabled:opacity-60"
            data-testid="signup-submit-button"
          >
            {loading ? "Submitting…" : "Request early access"}
          </button>
        </form>
      </div>
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[var(--night)] p-10 text-white order-1 lg:order-2 lg:flex">
        <div className="absolute inset-0 opacity-[0.16] [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)] [background-size:56px_56px]" />
        <div className="pointer-events-none absolute right-[-13rem] top-20 h-[18rem] w-[18rem] border border-[var(--saffron)]/18" />
        <div className="relative flex items-center justify-start">
          <Logo size={22} inverse flourish />
        </div>
        <div className="relative">
          <hr className="saffron-rule mb-6" />
          <div className="smallcaps text-[var(--saffron)]">Built around your workflow</div>
          <p className="mt-4 max-w-[30ch] font-serif text-[28px] font-medium leading-[1.12] tracking-[-0.018em]">
            Financial data context carried into reviewable documentation.
          </p>
          <ul className="mt-6 space-y-2.5 text-[13px] leading-[1.55] text-white/72">
            {[
              "Financial data and source-document context",
              "Desk-specific memos, reviews, and investment documents",
              "Controlled deployment planning and team support",
              "Review workflows for analysts and senior stakeholders",
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <Check className="mt-1 h-4 w-4 shrink-0 text-[var(--saffron)]" strokeWidth={2} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative flex items-end justify-between gap-6 border-t border-white/10 pt-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/40">
              Specific requests for discussion
            </div>
            <a href="mailto:hello@cosalpha.ai" className="mt-2 block w-fit font-mono text-[12px] uppercase tracking-[0.18em] text-white/72 transition-colors hover:text-[var(--saffron)]">
              hello@cosalpha.ai
            </a>
          </div>
          <div className="shrink-0 text-right text-[10px] font-semibold uppercase tracking-[0.22em] text-white/40">
            Sydney · Mumbai
          </div>
        </div>
      </div>
    </div>
  );
}
